/**
 * Two-tick dimension lines for SVG annotation.
 *
 * Both `HorizDim` (horizontal) and `VertDim` (vertical) emit the same
 * shape: a stroke between two end ticks, plus a label. They're shared by
 * GlyphInspectorPage and FoniCanvas; both pages were drawing these by
 * hand before. Defaults match the FoniCanvas outer-dimension style;
 * smaller fonts/ticks suit the per-glyph annotations in the inspector.
 */

export interface HorizDimProps {
    readonly x1: number
    readonly x2: number
    readonly y: number
    readonly label: string
    readonly clr: string
    /** Half-length of each end tick (px). Default 4. */
    readonly tickHalf?: number
    /** Line + tick stroke width. Default 1. */
    readonly strokeWidth?: number
    readonly fontSize?: number
    /** When true (default) the label sits above the line. */
    readonly labelAbove?: boolean
    /** Vertical distance from the line to the label baseline. Default 6 above / 12 below. */
    readonly labelOffset?: number
    /**
     * Hide the label when the segment is shorter than this many px. Useful
     * for per-element rows where labels would visually collide with ticks.
     */
    readonly minLabelWidth?: number
}

export function HorizDim({
    x1,
    x2,
    y,
    label,
    clr,
    tickHalf = 4,
    strokeWidth = 1,
    fontSize = 11,
    labelAbove = true,
    labelOffset,
    minLabelWidth
}: HorizDimProps) {
    const cx = (x1 + x2) / 2
    const tw = Math.abs(x2 - x1)
    const showLabel = minLabelWidth === undefined || tw > minLabelWidth
    const dy = labelOffset ?? (labelAbove ? 6 : 12)
    return (
        <g>
            <line
                x1={x1}
                y1={y - tickHalf}
                x2={x1}
                y2={y + tickHalf}
                stroke={clr}
                strokeWidth={strokeWidth}
            />
            <line
                x1={x2}
                y1={y - tickHalf}
                x2={x2}
                y2={y + tickHalf}
                stroke={clr}
                strokeWidth={strokeWidth}
            />
            <line
                x1={x1}
                y1={y}
                x2={x2}
                y2={y}
                stroke={clr}
                strokeWidth={strokeWidth}
            />
            {showLabel && (
                <text
                    x={cx}
                    y={labelAbove ? y - dy : y + dy}
                    textAnchor='middle'
                    fontSize={fontSize}
                    fontFamily='ui-monospace, monospace'
                    fill={clr}
                >
                    {label}
                </text>
            )}
        </g>
    )
}

export interface VertDimProps {
    readonly x: number
    readonly y1: number
    readonly y2: number
    readonly label: string
    readonly clr: string
    readonly tickHalf?: number
    readonly strokeWidth?: number
    readonly fontSize?: number
    /** Horizontal distance from the line to the label centre. Default 8. */
    readonly labelOffset?: number
}

export function VertDim({
    x,
    y1,
    y2,
    label,
    clr,
    tickHalf = 4,
    strokeWidth = 1,
    fontSize = 11,
    labelOffset = 8
}: VertDimProps) {
    const cy = (y1 + y2) / 2
    const lx = x - labelOffset
    return (
        <g>
            <line
                x1={x - tickHalf}
                y1={y1}
                x2={x + tickHalf}
                y2={y1}
                stroke={clr}
                strokeWidth={strokeWidth}
            />
            <line
                x1={x - tickHalf}
                y1={y2}
                x2={x + tickHalf}
                y2={y2}
                stroke={clr}
                strokeWidth={strokeWidth}
            />
            <line
                x1={x}
                y1={y1}
                x2={x}
                y2={y2}
                stroke={clr}
                strokeWidth={strokeWidth}
            />
            <text
                x={lx}
                y={cy}
                textAnchor='middle'
                dominantBaseline='middle'
                fontSize={fontSize}
                fontFamily='ui-monospace, monospace'
                fill={clr}
                transform={`rotate(-90, ${lx}, ${cy})`}
            >
                {label}
            </text>
        </g>
    )
}
